import { useQuery, useMutation } from '@tanstack/react-query';
import { Link, useParams } from 'react-router-dom';
import { useState } from 'react';
import {
  ArrowLeft,
  Mail,
  Phone,
  MapPin,
  ShoppingBag,
  MessageSquare,
  Ticket,
  Activity,
  Brain,
  Eye,
  Play,
  CheckCircle2,
  XCircle,
  CreditCard,
  Sparkles,
  Loader2,
  RefreshCw,
} from 'lucide-react';
import { apiClient } from '../lib/api.js';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/Card.js';
import { Badge } from '../components/ui/Badge.js';
import {
  cn,
  formatBRL,
  formatDateTime,
  formatPercent,
  formatRelative,
  orderStatusColor,
  priorityColor,
  priorityLabel,
  stageColor,
  stageLabel,
  temperatureColor,
  temperatureLabel,
} from '../lib/utils.js';

const eventIcons: Record<string, React.ReactNode> = {
  'visits.pageview': <Eye className="h-3.5 w-3.5" />,
  'vsl.play': <Play className="h-3.5 w-3.5" />,
  'vsl.pitch_reached': <Activity className="h-3.5 w-3.5 text-amber-400" />,
  'vsl.cta_clicked': <Activity className="h-3.5 w-3.5 text-blue-400" />,
  'checkout.started': <CreditCard className="h-3.5 w-3.5 text-purple-400" />,
  'payment.approved': <CheckCircle2 className="h-3.5 w-3.5 text-emerald-400" />,
  'payment.refused': <XCircle className="h-3.5 w-3.5 text-red-400" />,
  'message.received': <MessageSquare className="h-3.5 w-3.5 text-blue-400" />,
  'lifecycle.transition': <Activity className="h-3.5 w-3.5 text-violet-400" />,
};

function AISummaryCard({ contactId }: { contactId: string }): JSX.Element {
  const [text, setText] = useState<string | null>(null);
  const [warning, setWarning] = useState<string | null>(null);
  const summary = useMutation({
    mutationFn: () => apiClient.aiCustomerSummary(contactId),
    onSuccess: (r) => {
      setText(r.summary);
      setWarning(r.warning ?? null);
    },
  });
  return (
    <Card className="border-purple-500/20 bg-purple-500/5">
      <CardHeader>
        <CardTitle className="flex items-center justify-between gap-2">
          <span className="flex items-center gap-2 text-sm">
            <Sparkles className="h-4 w-4 text-purple-400" /> Resumo IA
          </span>
          <button
            type="button"
            onClick={() => summary.mutate()}
            disabled={summary.isPending}
            className="inline-flex items-center gap-1 px-2 py-1 rounded-md border border-purple-500/40 bg-purple-500/10 text-purple-300 text-[10px] font-medium hover:bg-purple-500/20 disabled:opacity-50"
          >
            {summary.isPending ? (
              <><Loader2 className="h-3 w-3 animate-spin" /> Gerando…</>
            ) : (
              <><RefreshCw className="h-3 w-3" /> {text ? 'Atualizar' : 'Gerar'}</>
            )}
          </button>
        </CardTitle>
      </CardHeader>
      <CardContent>
        {text ? (
          <p className="text-sm leading-relaxed whitespace-pre-wrap text-foreground/90">{text}</p>
        ) : (
          <p className="text-xs text-muted-foreground">
            Clique em "Gerar" pra IA escrever um briefing curto desse cliente: histórico, status atual e próxima ação sugerida.
          </p>
        )}
        {warning && (
          <div className="mt-2 text-[10px] text-amber-300 bg-amber-500/10 border border-amber-500/20 px-2 py-1 rounded">
            ⚠ {warning}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

export function ContactDetail(): JSX.Element {
  const { id } = useParams<{ id: string }>();
  const { data, isLoading, error } = useQuery({
    queryKey: ['contact', id],
    queryFn: () => apiClient.contactDetail(id!),
    enabled: !!id,
  });

  if (isLoading) return <div className="p-6 text-muted-foreground">Carregando...</div>;
  if (error || !data) return <div className="p-6 text-red-400">Contato não encontrado.</div>;

  const { contact, timeline, orders, conversations, tickets, scores } = data;

  return (
    <div className="p-6 max-w-7xl mx-auto">
      {/* Back */}
      <Link to="/contatos" className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground mb-4">
        <ArrowLeft className="h-4 w-4" /> Voltar para contatos
      </Link>

      {/* Header */}
      <div className="flex flex-wrap items-start gap-4 justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">{contact.fullName ?? 'Sem nome'}</h1>
          <div className="mt-2 flex flex-wrap items-center gap-3 text-sm text-muted-foreground">
            <span className="inline-flex items-center gap-1.5">
              <Mail className="h-3.5 w-3.5" /> {contact.email ?? '—'}
            </span>
            <span className="inline-flex items-center gap-1.5">
              <Phone className="h-3.5 w-3.5" /> {contact.phone ?? '—'}
            </span>
            <span className="inline-flex items-center gap-1.5">
              <MapPin className="h-3.5 w-3.5" /> {contact.geoCity}, {contact.geoState}, {contact.geoCountry}
            </span>
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          <Badge className={cn(stageColor[contact.lifecycleStage] ?? '')}>
            {stageLabel[contact.lifecycleStage] ?? contact.lifecycleStage}
          </Badge>
          <Badge className={cn(temperatureColor[contact.temperature] ?? '')}>
            {temperatureLabel[contact.temperature] ?? contact.temperature}
          </Badge>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Coluna esquerda: timeline */}
        <div className="lg:col-span-2 space-y-6">
          {/* Resumo IA */}
          {id && <AISummaryCard contactId={id} />}

          {/* Timeline */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Activity className="h-4 w-4" /> Timeline ({timeline.length} eventos)
              </CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              <div className="max-h-[500px] overflow-y-auto">
                {timeline.length === 0 ? (
                  <div className="p-6 text-center text-sm text-muted-foreground">Sem eventos registrados.</div>
                ) : (
                  <ol className="divide-y divide-border">
                    {timeline.map((e) => (
                      <li key={e.id} className="flex gap-3 p-3 hover:bg-muted/30">
                        <div className="flex h-6 w-6 items-center justify-center rounded-full bg-muted shrink-0">
                          {eventIcons[e.eventType] ?? <Activity className="h-3 w-3" />}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-baseline justify-between gap-2">
                            <span className="font-medium text-sm truncate">{e.eventType}</span>
                            <span className="text-xs text-muted-foreground shrink-0">{formatRelative(e.occurredAt)}</span>
                          </div>
                          <div className="text-xs text-muted-foreground">
                            via {e.sourceSystem}
                            {e.payload && typeof e.payload === 'object' && 'url' in e.payload ? (
                              <> · {String((e.payload as Record<string, unknown>).url)}</>
                            ) : null}
                          </div>
                        </div>
                      </li>
                    ))}
                  </ol>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Pedidos */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <ShoppingBag className="h-4 w-4" /> Pedidos ({orders.length})
              </CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              {orders.length === 0 ? (
                <div className="p-6 text-center text-sm text-muted-foreground">Nenhum pedido.</div>
              ) : (
                <table className="w-full text-sm">
                  <thead className="text-xs text-muted-foreground bg-muted/30 border-b border-border">
                    <tr>
                      <th className="px-4 py-2 text-left font-medium">Produto</th>
                      <th className="px-4 py-2 text-left font-medium">Tipo</th>
                      <th className="px-4 py-2 text-left font-medium">Pgto</th>
                      <th className="px-4 py-2 text-right font-medium">Valor</th>
                      <th className="px-4 py-2 text-left font-medium">Status</th>
                      <th className="px-4 py-2 text-right font-medium">Quando</th>
                    </tr>
                  </thead>
                  <tbody>
                    {orders.map((o) => (
                      <tr key={o.id} className="border-b border-border last:border-0">
                        <td className="px-4 py-2">{o.productName}</td>
                        <td className="px-4 py-2 text-xs text-muted-foreground">{o.productKind}</td>
                        <td className="px-4 py-2 text-xs uppercase">{o.paymentMethod}</td>
                        <td className="px-4 py-2 text-right font-mono">{formatBRL(o.amountGrossCents)}</td>
                        <td className="px-4 py-2">
                          <Badge className={cn(orderStatusColor[o.status] ?? '')}>{o.status}</Badge>
                        </td>
                        <td className="px-4 py-2 text-right text-xs text-muted-foreground">
                          {formatRelative(o.approvedAt ?? o.createdAt)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </CardContent>
          </Card>

          {/* Conversas */}
          {conversations.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <MessageSquare className="h-4 w-4" /> Conversas ({conversations.length})
                </CardTitle>
              </CardHeader>
              <CardContent className="p-0 divide-y divide-border">
                {conversations.map((c) => (
                  <Link key={c.id} to={`/inbox?id=${c.id}`} className="block px-4 py-3 hover:bg-muted/30">
                    <div className="flex items-center justify-between gap-2">
                      <div className="flex items-center gap-2">
                        <Badge className={cn(priorityColor[c.priority] ?? '')}>{priorityLabel[c.priority]}</Badge>
                        <span className="text-xs uppercase text-muted-foreground">{c.channel}</span>
                        <Badge variant="outline">{c.status}</Badge>
                      </div>
                      <span className="text-xs text-muted-foreground">{formatRelative(c.updatedAt)}</span>
                    </div>
                    {c.intentClassification && (
                      <div className="mt-1 text-xs text-muted-foreground">Intent: {c.intentClassification}</div>
                    )}
                  </Link>
                ))}
              </CardContent>
            </Card>
          )}

          {/* Tickets */}
          {tickets.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Ticket className="h-4 w-4" /> Tickets ({tickets.length})
                </CardTitle>
              </CardHeader>
              <CardContent className="p-0 divide-y divide-border">
                {tickets.map((t) => (
                  <div key={t.id} className="px-4 py-3">
                    <div className="flex items-center justify-between gap-2">
                      <div className="flex items-center gap-2">
                        <Badge className={cn(priorityColor[t.priority] ?? '')}>{priorityLabel[t.priority]}</Badge>
                        <Badge variant="outline">{t.type}</Badge>
                        <Badge variant="outline">{t.status}</Badge>
                      </div>
                      <span className="text-xs text-muted-foreground">{formatRelative(t.createdAt)}</span>
                    </div>
                    <div className="mt-1 text-sm">{t.subject}</div>
                  </div>
                ))}
              </CardContent>
            </Card>
          )}
        </div>

        {/* Coluna direita: scores + meta */}
        <div className="space-y-6">
          {/* Scores ML */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Brain className="h-4 w-4" /> Scores Preditivos
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              {scores.length === 0 ? (
                <div className="text-xs text-muted-foreground">Sem scores. Modelos rodam em Fase 4.</div>
              ) : (
                scores.map((s) => (
                  <div key={s.modelName}>
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-xs">{s.modelName}</span>
                      <span className="text-xs font-mono">{formatPercent(s.score)}</span>
                    </div>
                    <div className="h-1.5 rounded-full bg-muted overflow-hidden">
                      <div
                        className="h-full bg-primary"
                        style={{ width: `${Math.min(Number(s.score) * 100, 100)}%` }}
                      />
                    </div>
                  </div>
                ))
              )}
            </CardContent>
          </Card>

          {/* Identity */}
          <Card>
            <CardHeader>
              <CardTitle>Identidade Tracking</CardTitle>
            </CardHeader>
            <CardContent className="space-y-2 text-xs">
              <Field label="FBP" value={contact.fbp} mono />
              <Field label="FBC" value={contact.fbc} mono />
              <Field label="External ID" value={contact.externalId} mono />
              <Field label="Primeiro acesso" value={formatDateTime(contact.firstSeenAt)} />
              <Field label="Último acesso" value={formatRelative(contact.lastSeenAt)} />
            </CardContent>
          </Card>

          {/* Origem */}
          {contact.metadata && (
            <Card>
              <CardHeader>
                <CardTitle>Origem</CardTitle>
              </CardHeader>
              <CardContent className="space-y-2 text-xs">
                {(() => {
                  const meta = contact.metadata as Record<string, unknown>;
                  const utm = meta.utm as Record<string, unknown> | undefined;
                  if (!utm) return <div className="text-muted-foreground">—</div>;
                  return (
                    <>
                      <Field label="UTM Source" value={utm.utm_source as string} />
                      <Field label="UTM Campaign" value={utm.utm_campaign as string} />
                      <Field label="UTM Content" value={utm.utm_content as string} />
                    </>
                  );
                })()}
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
}

function Field({ label, value, mono = false }: { label: string; value?: string | null; mono?: boolean }): JSX.Element {
  return (
    <div className="flex justify-between gap-2">
      <span className="text-muted-foreground shrink-0">{label}</span>
      <span className={cn('text-right truncate', mono && 'font-mono text-[11px]')}>{value ?? '—'}</span>
    </div>
  );
}
